// This defines the default webapp implementation of `PerseusDependencies`.
import {MathJax} from "@repo/component-tex";
import {InitialRequestUrl, RequestInfo} from "@repo/shared-core";
import {JIPT, maybeUnescape} from "@repo/translate";
import {staticUrl} from "@repo/urls";

import {getData, setData} from "./data-attr.ts";
import type {
    JiptLabelStore,
    JiptRenderer,
    PerseusDependencies,
    PerseusDependenciesV2,
} from "./perseus.ts";
import {LoggingAdapter} from "./webapp-deps/logging-adapter.ts";
import {useVideo} from "./webapp-deps/use-video.ts";

interface JiptLabel {
    label: any;
    useMath: any;
}

/**
 * graphie-movables.jsx
 */
export const _graphieMovablesJiptLabels: Array<JiptLabel> = []; // exported for testing only
JIPT.domInsertChecks.push(function (text, node, attribute) {
    const index = getData(node, "jipt-label-movables-index");
    if (node && typeof index !== "undefined") {
        const {label, useMath} = _graphieMovablesJiptLabels[index];
        label.text("");
        text = maybeUnescape(text);
        if (useMath === "undefined" || useMath) {
            const mathRegex = /^\$(.*)\$$/;
            const match = text.match(mathRegex);
            const mathText = match
                ? match[1]
                : "\\color{red}{\\text{Invalid Math}}";
            label.processMath(mathText, true);
        } else {
            label.processText(text);
        }

        return false;
    }
    return text;
});

const graphieMovablesJiptLabels: JiptLabelStore = {
    addLabel: (elem, useMath) => {
        setData(
            elem,
            "jipt-label-movables-index",
            String(_graphieMovablesJiptLabels.length),
        );
        _graphieMovablesJiptLabels.push({
            label: elem,
            useMath: useMath,
        });
    },
};

/**
 * svg-image.jsx
 */
export const _svgImageJiptLabels: Array<JiptLabel> = []; // exported for testing only
JIPT.domInsertChecks.push(function (text, node, attribute) {
    const index = getData(node, "jipt-label-index");
    if (node && typeof index !== "undefined") {
        const {label, useMath} = _svgImageJiptLabels[index];

        label.text("");

        text = maybeUnescape(text);

        if (useMath) {
            const mathRegex = /^\$(.*)\$$/;
            const match = text.match(mathRegex);
            const mathText = match
                ? match[1]
                : "\\color{red}{\\text{Invalid Math}}";
            label.processMath(mathText, true);
        } else {
            label.processText(text);
        }

        return false;
    }
    return text;
});
const svgImageJiptLabels: JiptLabelStore = {
    addLabel: (elem, useMath) => {
        setData(elem, "jipt-label-index", String(_svgImageJiptLabels.length));
        _svgImageJiptLabels.push({
            label: elem,
            useMath: useMath,
        });
    },
};

/**
 * renderer.jsx
 */
// When using crowdin's jipt (Just in place translation), we need to keep a
// registry of crowdinId's to component so that we can update the
// component's state as the translator enters their translation.
export const _rendererTranslationComponents: Array<
    JiptRenderer | null | undefined
> = []; // exported for testing only
// We add a function that will get called whenever jipt says the dom needs
// to be updated
JIPT.domInsertChecks.push(function (text, node, attribute) {
    // NOTE: This code is a bit weird.  It gets the data-perseus-component-index
    // on the original node, but when Perseus updates the DOM, that data attribute
    // appears on children of the original node.  `getData` compensates for this
    // by caching the data attribute values on the original node and then returning
    // those values for subsequent calls to `getData`.  AFAICT, the children's data
    // attributes are completely ignored.
    const paragraphIndex = getData(node, "perseus-paragraph-index");
    const index = getData(node, "perseus-component-index");

    // We only update if we had added an index onto the node's data.
    if (node && typeof index !== "undefined") {
        const component = _rendererTranslationComponents[index];

        if (!component) {
            // The component has disappeared, so we tell jipt not to try
            // and insert anything
            return false;
        }
        // Jipt sometimes sends down the escaped translation, so we need to
        // unescape \\t to \t among other characters here
        text = maybeUnescape(text);

        component.replaceJiptContent(text, paragraphIndex);

        // Return false to tell jipt not to insert anything into the DOM
        // itself, otherwise it will mess up what React expects there to be
        return false;
    }
    // The string updated wasn't part of perseus, so we tell jipt to just
    // insert the translation as-is.
    return text;
});
const rendererTranslationComponents = {
    addComponent: (renderer: JiptRenderer) =>
        _rendererTranslationComponents.push(renderer) - 1,
    removeComponentAtIndex: (index: number) => {
        _rendererTranslationComponents[index] = null;
    },
} as const;

// make TypeScript happy now that we removed
// domInsertChecks from the Perseus API
const {domInsertChecks: _, ...restJIPT} = JIPT;

export const webappDependenciesV2: Readonly<PerseusDependenciesV2> = {
    analytics: {
        onAnalyticsEvent: async (e) => {},
    },
    useVideo,
};

export const webappDependencies: PerseusDependencies = {
    // JIPT
    JIPT: restJIPT,
    graphieMovablesJiptLabels,
    svgImageJiptLabels,
    rendererTranslationComponents,

    TeX: MathJax,

    // URLs
    staticUrl,

    InitialRequestUrl: {
        origin: InitialRequestUrl.origin,
        host: InitialRequestUrl.host,
        protocol: InitialRequestUrl.protocol,
    },

    isDevServer: RequestInfo.IS_DEV_SERVER,
    kaLocale: RequestInfo.kaLocale,

    // Instrumentation
    Log: new LoggingAdapter(),
};
